import { useCallback, useMemo } from "react";
import Select, { MultiValue, OptionProps, components } from "react-select";
import { StateManagerProps } from "react-select/dist/declarations/src/useStateManager";
import { Box, forwardRef } from "@chakra-ui/react";
import { keyBy } from "lodash";
import { useLazyQuery } from "@apollo/client";
import { DocumentType, getFragmentData, gql } from "__generated__/gql";
import useSelectStyles, { defaultComponents } from "utils/useSelectStyles";
import asyncVoidHandler from "utils/asyncVoidHandler";
import { useBrandQuery } from "utils/apollo/hooks/brand";

const optionFragment = gql(/* GraphQL */ `
  fragment ProductCategoriesSelectOption on ProductCategoryDepartmentSubDepartment {
    id
    qualifiedLabel
  }
`);

const valuesQuery = gql(/* GraphQL */ `
  query ProductCategoriesSelectValues($ids: [UUID!]!) {
    productCategoryDepartmentSubDepartmentsList(filter: { id: { in: $ids } }) {
      ...ProductCategoriesSelectOption
    }
  }
`);

const optionsQuery = gql(/* GraphQL */ `
  query ProductCategoriesSelectOptions {
    productDepartmentsList {
      id
      label: name
      options: productCategoryDepartmentSubDepartmentsList {
        id
        categoryName
        qualifiedLabelPref
      }
    }
  }
`);

type ValueOption = Omit<
  DocumentType<typeof optionFragment>,
  "__typename" | " $fragmentName"
>;

type OptionsOption = NonNullable<
  DocumentType<typeof optionsQuery>["productDepartmentsList"]
>[number]["options"][number];

type Option = ValueOption | OptionsOption;

function getIsValueOption(option: Option): option is ValueOption {
  return "qualifiedLabel" in option;
}

function CustomOption(props: OptionProps<Option, true>) {
  const { data } = props;
  if (getIsValueOption(data)) return <components.Option {...props} />;
  return (
    <components.Option {...props}>
      <Box fontSize="xs" color="gray.500">
        {data.qualifiedLabelPref}
      </Box>
      <Box fontSize="sm">{data.categoryName}</Box>
    </components.Option>
  );
}

function getOptionValue(option: Option) {
  return option.id;
}

function getOptionLabel(option: Option) {
  if (getIsValueOption(option)) return option.qualifiedLabel ?? " ";
  return option.categoryName ?? " ";
}

type Props = Omit<StateManagerProps<Option, true>, "onChange" | "value"> & {
  onChange: (productCategoryIds: UUID[]) => void;
  value?: string[] | null;
};

const ProductCategoriesSelect = forwardRef<Props, "input">(
  ({ onChange, styles, value: rawValue, ...rest }, ref) => {
    const styleProps = useSelectStyles<Option, true>(styles);

    const { data: valuesData } = useBrandQuery(valuesQuery, {
      skip: !rawValue?.length,
      variables: rawValue ? { ids: rawValue } : undefined,
    });

    const [fetchOptions, { data, loading: isLoading }] =
      useLazyQuery(optionsQuery);

    const options = data?.productDepartmentsList ?? undefined;

    const value = useMemo<ValueOption[]>(() => {
      if (!rawValue?.length) return [];
      const byId = keyBy(
        getFragmentData(
          optionFragment,
          valuesData?.productCategoryDepartmentSubDepartmentsList,
        ),
        "id",
      );
      return rawValue.map(
        (id) => byId[id] ?? { id, qualifiedLabel: valuesData ? " " : "…" },
      );
    }, [rawValue, valuesData]);

    const handleChange = useCallback(
      (newOptions: MultiValue<Option>) => {
        onChange?.(newOptions.map(getOptionValue));
      },
      [onChange],
    );

    return (
      <Select<Option, true>
        isMulti
        {...styleProps}
        {...{ options, value, getOptionValue, getOptionLabel, isLoading, ref }}
        components={{ ...defaultComponents, Option: CustomOption }}
        placeholder="Select categories…"
        noOptionsMessage={() => "No additional categories are available"}
        onMenuOpen={asyncVoidHandler(() => fetchOptions())}
        onChange={handleChange}
        backspaceRemovesValue={false}
        {...rest}
      />
    );
  },
);

export default ProductCategoriesSelect;
